/**
 * Inline CSS styles for the CMP banner and preferences modal.
 * Kept as a string to avoid external CSS dependencies and keep bundle small.
 */

import { resolveBannerTheme, type AdminThemeJson } from "./theme-resolve";

type ThemeInput = AdminThemeJson;

function getBannerPositionStyles(position: string): string {
  switch (position) {
    case "top":
      return "top: 0; bottom: auto; left: 0; right: 0;";
    case "center":
      return [
        "top: 50%; left: 50%; right: auto;",
        "transform: translate(-50%, -50%);",
        "bottom: auto; width: 90%; max-width: 560px;",
      ].join(" ");
    case "bottom-left":
      return [
        "bottom: 16px; left: 16px; right: auto; top: auto;",
        "width: calc(100vw - 32px); max-width: 400px; min-width: 280px;",
        "border-radius: 12px;",
        "padding: 16px 18px;",
      ].join(" ");
    case "bottom":
    default:
      return "bottom: 0; top: auto; left: 0; right: 0;";
  }
}

export function getStyles(theme?: ThemeInput): string {
  const resolved = resolveBannerTheme(theme);
  const primary = resolved.primaryColor;
  const bg = resolved.backgroundColor;
  const text = resolved.textColor;
  const radius = resolved.borderRadius;
  const position = resolved.position;
  const dntLinkColor = resolved.doNotSellLinkColor;
  const outline = resolved.useOutlineButtons;

  const acceptStyle = outline
    ? `background: ${resolved.acceptBg}; color: ${resolved.acceptText}; border: 1px solid ${resolved.acceptBorder};`
    : `background: ${resolved.acceptBg}; color: ${resolved.acceptText}; border: none;`;

  const rejectStyle = outline
    ? `background: ${resolved.rejectBg}; color: ${resolved.rejectText}; border: 1px solid ${resolved.rejectBorder};`
    : `background: ${resolved.rejectBg}; color: ${resolved.rejectText}; border: none;`;

  const manageStyle = outline
    ? `background: ${resolved.manageBg}; color: ${resolved.manageText}; border: 1px solid ${resolved.manageBorder};`
    : `background: ${resolved.manageBg}; color: ${resolved.manageText}; border: 1px solid ${resolved.manageBorder};`;

  const positionStyles = getBannerPositionStyles(position);

  return `
    .cmp-page-blocker {
      position: fixed;
      inset: 0;
      z-index: 99997;
      background: rgba(0, 0, 0, 0.45);
      touch-action: none;
    }
    .cmp-overlay {
      position: fixed;
      inset: 0;
      background: rgba(0,0,0,0.5);
      z-index: 99998;
    }
    .cmp-banner {
      position: fixed;
      left: 0;
      right: 0;
      ${positionStyles}
      z-index: 99999;
      background: ${bg};
      color: ${text};
      box-shadow: ${
        position === "bottom-left"
          ? "0 4px 24px rgba(0,0,0,0.2)"
          : position === "center"
            ? "0 4px 32px rgba(0,0,0,0.2)"
            : "0 -2px 16px rgba(0,0,0,0.15)"
      };
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      font-size: 14px;
      line-height: 1.5;
      padding: 20px 24px;
      box-sizing: border-box;
    }
    .cmp-banner__content {
      max-width: 1200px;
      margin: 0 auto;
    }
    .cmp-banner__notice {
      background: #fff3cd;
      color: #856404;
      border: 1px solid #ffc107;
      border-radius: ${radius};
      padding: 8px 12px;
      font-size: 13px;
      margin-bottom: 12px;
      line-height: 1.4;
    }
    .cmp-banner__title {
      font-size: 18px;
      font-weight: 700;
      margin: 0 0 8px 0;
      color: ${text};
    }
    .cmp-banner__desc {
      margin: 0 0 16px 0;
      color: ${text};
      opacity: 0.85;
    }
    .cmp-banner__do-not-sell-wrap {
      margin-bottom: 12px;
    }
    .cmp-banner__do-not-sell-link {
      color: ${dntLinkColor};
      text-decoration: underline;
      font-size: 12px;
    }
    .cmp-banner__actions {
      display: flex;
      gap: 10px;
      flex-wrap: wrap;
    }
    .cmp-btn {
      padding: 10px 20px;
      border: none;
      border-radius: ${radius};
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: opacity 0.2s;
      font-family: inherit;
    }
    .cmp-btn:hover {
      opacity: 0.85;
    }
    .cmp-btn--accept {
      ${acceptStyle}
    }
    .cmp-btn--reject {
      ${rejectStyle}
    }
    .cmp-btn--manage {
      ${manageStyle}
    }
    .cmp-btn--save {
      background: ${primary};
      color: #fff;
    }
    .cmp-banner__brand {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 4px;
      margin-top: 12px;
      font-size: 10px;
      opacity: 0.7;
      color: ${text};
    }
    .cmp-banner__brand-mark {
      height: 16px;
      width: auto;
    }
    /* Preferences modal */
    .cmp-prefs {
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 100000;
      background: ${bg};
      color: ${text};
      border-radius: 12px;
      box-shadow: 0 4px 32px rgba(0,0,0,0.2);
      padding: 24px;
      width: 90%;
      max-width: 500px;
      max-height: 80vh;
      overflow-y: auto;
      font-family: inherit;
      box-sizing: border-box;
    }
    .cmp-prefs__title {
      font-size: 20px;
      font-weight: 700;
      margin: 0 0 16px 0;
    }
    .cmp-prefs__intro {
      font-size: 14px;
      line-height: 1.5;
      margin: 0 0 16px 0;
      opacity: 0.9;
    }
    .cmp-prefs__category {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 0;
      border-bottom: 1px solid rgba(0,0,0,0.1);
    }
    .cmp-prefs__cat-info {
      flex: 1;
    }
    .cmp-prefs__cat-name {
      font-weight: 600;
      font-size: 14px;
    }
    .cmp-prefs__cat-desc {
      font-size: 12px;
      opacity: 0.7;
      margin-top: 2px;
    }
    .cmp-toggle {
      position: relative;
      width: 44px;
      height: 24px;
      flex-shrink: 0;
      margin-left: 16px;
    }
    .cmp-toggle input {
      opacity: 0;
      width: 0;
      height: 0;
    }
    .cmp-toggle__slider {
      position: absolute;
      cursor: pointer;
      inset: 0;
      background: #ccc;
      border-radius: 24px;
      transition: background 0.3s;
    }
    .cmp-toggle__slider::before {
      content: "";
      position: absolute;
      height: 18px;
      width: 18px;
      left: 3px;
      bottom: 3px;
      background: white;
      border-radius: 50%;
      transition: transform 0.3s;
    }
    .cmp-toggle input:checked + .cmp-toggle__slider {
      background: ${primary};
    }
    .cmp-toggle input:checked + .cmp-toggle__slider::before {
      transform: translateX(20px);
    }
    .cmp-toggle input:disabled + .cmp-toggle__slider {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .cmp-prefs__footer {
      display: flex;
      gap: 10px;
      justify-content: flex-end;
      margin-top: 16px;
    }
    .cmp-prefs__cookies {
      border-bottom: 1px solid rgba(0,0,0,0.08);
      padding: 8px 0 10px 0;
    }
    .cmp-prefs__cookie {
      display: flex;
      justify-content: space-between;
      gap: 10px;
      padding: 8px 0 8px 12px;
      border-left: 2px solid rgba(0,0,0,0.08);
      margin: 0 0 0 4px;
    }
    .cmp-prefs__cookie-info {
      min-width: 0;
      flex: 1;
    }
    .cmp-prefs__cookie-name {
      font-size: 12px;
      font-weight: 600;
    }
    .cmp-prefs__cookie-meta {
      font-size: 11px;
      opacity: 0.7;
      word-break: break-all;
    }
    .cmp-prefs__cookie-desc {
      margin-top: 2px;
      font-size: 11px;
      opacity: 0.8;
    }
    .cmp-consent-widget {
      position: fixed;
      left: 16px;
      bottom: 16px;
      z-index: 99996;
      width: 48px;
      height: 48px;
      border: none;
      border-radius: 50%;
      background: ${primary};
      color: #fff;
      box-shadow: 0 2px 12px rgba(0,0,0,0.25);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      transition: transform 0.15s ease, box-shadow 0.15s ease;
      font-family: inherit;
    }
    .cmp-consent-widget:hover {
      transform: scale(1.06);
      box-shadow: 0 4px 16px rgba(0,0,0,0.3);
    }
    .cmp-consent-widget:focus-visible {
      outline: 2px solid ${primary};
      outline-offset: 3px;
    }
  `;
}
